import React, { useLayoutEffect, useMemo, useRef, useState } from "react";
import { eventStep, loopBeats, loopSeconds, playheadX, withoutEmptyLanes } from "../../lib/patternGeometry";

// The kit page's pattern preview, drawn as the TrapMan plugin's own window:
// top bar, lane panel, step grid, the lower editor strip and its piano roll.
// Display only: nothing here is clickable, it exists so a visitor sees the
// exact interface the kit opens in. Every size and colour is lifted from the
// plugin source (PluginEditor.h, TopBarComponent, LanePanelComponent,
// PatternGridComponent, TrapManLookAndFeel), so when those change, this is
// the file to update.
//
// Data the site has: each lane's generic category and the pattern's events
// (step, length, velocity). What it does not have, because the grid never
// carries it (see build_grid in backend/services/preset_render.py), is each
// lane's real colour, volume, pan or note, nor the pattern's FX settings; the
// plugin's defaults stand in for those.

// ---------------------------------------------------------------- geometry
const DESIGN_W = 1210; // about the plugin window in the manual captures; scaled to fit
const TOPBAR_H = 36; // kTopBarHeight
const LANE_PANEL_W = 240; // kLanePanelWidth
const RULER_H = 20; // kRulerHeight
const CELL_H = 36; // kCellHeight == kLaneRowHeight
const SCROLLBAR_H = 18;
const EDITOR_HEADER_H = 36;
const KEYS_W = 50;
const KEY_ROW_H = 16;
const PIANO_ROWS = ["F2", "E2", "D#2", "D2", "C#2", "C2", "B1", "A#1", "A1", "G#1", "G1", "F#1"];
const ROOT_ROW = PIANO_ROWS.indexOf("C2"); // an unshifted drum hit plays its sample's root, drawn on C2
const PIANO_H = RULER_H + PIANO_ROWS.length * KEY_ROW_H;
const STEPS_PER_BEAT = 4;
const MIN_FIT_SCALE = 0.6;
const VISIBLE_BARS = 2; // like the plugin with a 4 BARS pattern open
const CELL_CORNER = 4; // kCellCorner
const BUTTON_CORNER = 4.5; // kButtonCorner
const FADER_W = 72; // kVolumeFaderWidth
const SOLO_W = 28; // kSoloButtonWidth
const PAN_SIZE = 18; // kPanKnobSize
const PAN_GAP = 8; // kPanGap
const UNITY_FADER_POS = 0.75; // kUnityFaderPos: 0 dB sits at 3/4 of the fader

// ------------------------------------------------------------------ colour
const BG = "#151517"; // getBackgroundColour
const PANEL = "#18181A"; // getPanelColour
const TOOLBAR = "#1D1D20";
const DIVIDER = "#292929"; // getDividerColour
const GRID_LINE = "#262626"; // getGridLineColour
const GRID_LINE_BRIGHT = "#303030"; // getGridLineBrightColour
const TEXT = "#F2F2F2"; // getTextColour
const PLAYHEAD = "#F8F8F8"; // getPlayheadColour
const FONT = "'Lucida Grande', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";
const METER_GRADIENT = "linear-gradient(90deg, #2ECC71 0%, #F1C40F 55%, #E67E22 78%, #E74C3C 100%)";
const METER_DECAY = 0.88; // per tick of the plugin's 30 Hz meter timer
const METER_HEADROOM = 0.92;

// Same HSV -> RGB as juce::Colour::fromHSV, channels in 0..255.
function hsvToRgb(h, s, v) {
  const i = Math.floor(h * 6);
  const f = h * 6 - i;
  const p = v * (1 - s);
  const q = v * (1 - f * s);
  const t = v * (1 - (1 - f) * s);
  const [r, g, b] = [
    [v, t, p],
    [q, v, p],
    [p, v, t],
    [p, q, v],
    [t, p, v],
    [v, p, q],
  ][((i % 6) + 6) % 6];
  return [r, g, b].map((c) => Math.round(c * 255));
}
// The plugin's lane palette (getLanePaletteColour): 18 hues from the top
// row, in one of its three modes, which shift the start column. Classic runs
// red to green, green-shifted green to blue, pink-shifted purple to red.
export const PALETTE_MODES = ["classic", "greenShifted", "pinkShifted"];
const MODE_OFFSET = { classic: 0, greenShifted: 7, pinkShifted: 14 }; // kGreenShiftColumn, kPinkShiftColumn
const laneColour = (i, mode = "classic") =>
  hsvToRgb((((i % 18) + (MODE_OFFSET[mode] ?? 0)) % 18) / 18, 0.85, 1.0);
// juce::Colour::brighter / darker.
const brighter = (c, a) => c.map((x) => Math.round(255 - (255 - x) / (1 + a)));
const darker = (c, a) => c.map((x) => Math.round(x / (1 + a)));
const css = ([r, g, b], a = 1) => `rgba(${r}, ${g}, ${b}, ${a})`;
const hex = (h) => [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16));

// ----------------------------------------------------------- canvas helpers
function rr(ctx, x, y, w, h, r) {
  const rad = Math.max(0, Math.min(r, w / 2, h / 2));
  ctx.beginPath();
  ctx.moveTo(x + rad, y);
  ctx.arcTo(x + w, y, x + w, y + h, rad);
  ctx.arcTo(x + w, y + h, x, y + h, rad);
  ctx.arcTo(x, y + h, x, y, rad);
  ctx.arcTo(x, y, x + w, y, rad);
  ctx.closePath();
}
function vGrad(ctx, y0, y1, top, bottom) {
  const g = ctx.createLinearGradient(0, y0, 0, y1);
  g.addColorStop(0, top);
  g.addColorStop(1, bottom);
  return g;
}
function line(ctx, x0, y0, x1, y1, colour, width = 1) {
  ctx.strokeStyle = colour;
  ctx.lineWidth = width;
  ctx.beginPath();
  ctx.moveTo(x0, y0);
  ctx.lineTo(x1, y1);
  ctx.stroke();
}
function sizeCanvas(canvas, w, h) {
  const dpr = window.devicePixelRatio || 1;
  const tw = Math.round(w * dpr);
  const th = Math.round(h * dpr);
  if (canvas.width !== tw || canvas.height !== th) {
    canvas.width = tw;
    canvas.height = th;
  }
  const ctx = canvas.getContext("2d");
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  return ctx;
}

// getButtonPlateBounds / getButtonFaceBounds, fixed insets exactly as the
// plugin: zoomed out, its pads narrow into upright capsules, and so do these.
function plateOf(x, y, w, h) {
  return { x: x + 7.5, y: y + 4.5, w: Math.max(2, w - 15), h: Math.max(2, h - 9) };
}
function faceOf(p, pressed) {
  const f = { x: p.x + 2, y: p.y + 2, w: Math.max(1, p.w - 4), h: p.h - 4 };
  f.h -= Math.min(3, f.h * 0.12);
  if (pressed) f.y += 1;
  return f;
}

// drawInactiveStepButton
function drawInactive(ctx, cell, emphasise) {
  const p = plateOf(cell.x, cell.y, cell.w, cell.h);
  const f = faceOf(p, false);
  ctx.fillStyle = "rgba(0, 0, 0, 0.28)";
  rr(ctx, p.x, p.y + 1.5, p.w, p.h, BUTTON_CORNER);
  ctx.fill();
  ctx.fillStyle = vGrad(ctx, p.y, p.y + p.h, emphasise ? "#484848" : "#404040", emphasise ? "#222222" : "#1C1C1C");
  rr(ctx, p.x, p.y, p.w, p.h, BUTTON_CORNER);
  ctx.fill();
  ctx.strokeStyle = "rgba(255, 255, 255, 0.08)";
  ctx.lineWidth = 1;
  rr(ctx, p.x + 0.5, p.y + 0.5, p.w - 1, p.h - 1, BUTTON_CORNER);
  ctx.stroke();
  ctx.fillStyle = vGrad(ctx, f.y, f.y + f.h, emphasise ? "#3A3A3A" : "#333333", emphasise ? "#222222" : "#1C1C1C");
  rr(ctx, f.x, f.y, f.w, f.h, BUTTON_CORNER - 1);
  ctx.fill();
  line(ctx, f.x + 1.5, f.y + 1, f.x + f.w - 1.5, f.y + 1, "rgba(255, 255, 255, 0.12)");
  line(ctx, f.x + 1.5, f.y + f.h - 0.75, f.x + f.w - 1.5, f.y + f.h - 0.75, "rgba(0, 0, 0, 0.30)");
}

// drawActiveStepButton
function drawActive(ctx, cell, colour, velocity, emphasise) {
  const p = plateOf(cell.x, cell.y, cell.w, cell.h);
  const f = faceOf(p, true);
  const capTop = brighter(colour, 0.22);
  const capBottom = darker(colour, 0.4);
  ctx.fillStyle = "rgba(0, 0, 0, 0.34)";
  rr(ctx, p.x, p.y + 1.2, p.w, p.h, BUTTON_CORNER);
  ctx.fill();
  ctx.fillStyle = vGrad(ctx, p.y, p.y + p.h, emphasise ? "#707070" : "#686868", emphasise ? "#2C2C2C" : "#272727");
  rr(ctx, p.x, p.y, p.w, p.h, BUTTON_CORNER);
  ctx.fill();
  ctx.fillStyle = "rgba(22, 22, 22, 0.82)";
  rr(ctx, p.x + 1, p.y + 1, p.w - 2, p.h - 2, BUTTON_CORNER - 0.5);
  ctx.fill();
  ctx.fillStyle = vGrad(ctx, f.y, f.y + f.h, css(capTop), css(capBottom));
  rr(ctx, f.x, f.y, f.w, f.h, BUTTON_CORNER - 1.2);
  ctx.fill();
  line(ctx, f.x + 1.5, f.y + 1, f.x + f.w - 1.5, f.y + 1, "rgba(255, 255, 255, 0.30)");
  const slot = { x: f.x + 3, y: f.y + f.h - 5, w: Math.max(4, f.w - 6), h: 2.5 };
  ctx.fillStyle = "rgba(16, 16, 16, 0.95)";
  rr(ctx, slot.x, slot.y - 0.5, slot.w, slot.h + 1, 1.4);
  ctx.fill();
  const v = Math.max(1, Math.min(127, Number.isFinite(velocity) ? velocity : 100));
  const ledW = 3 + ((v - 1) / 126) * (slot.w - 3);
  const led = ctx.createLinearGradient(slot.x, 0, slot.x + ledW, 0);
  led.addColorStop(0, css(brighter(capTop, 0.35)));
  led.addColorStop(1, css(brighter(capBottom, 0.1)));
  ctx.fillStyle = led;
  rr(ctx, slot.x, slot.y, Math.min(ledW, slot.w), slot.h, 1.25);
  ctx.fill();
  ctx.strokeStyle = "rgba(255, 255, 255, 0.22)";
  ctx.lineWidth = 0.8;
  rr(ctx, f.x + 0.5, f.y + 0.5, f.w - 1, f.h - 1, BUTTON_CORNER - 1.6);
  ctx.stroke();
}

function drawRuler(ctx, w, offset, pxPerStep, totalSteps, stepsPerBar) {
  ctx.fillStyle = BG;
  ctx.fillRect(0, 0, w, RULER_H);
  line(ctx, 0, RULER_H - 0.5, w, RULER_H - 0.5, DIVIDER);
  ctx.font = `bold 10px ${FONT}`;
  ctx.textBaseline = "top";
  const first = Math.max(0, Math.floor(offset / pxPerStep));
  const last = Math.min(totalSteps, Math.ceil((offset + w) / pxPerStep));
  for (let s = first; s <= last; s += 1) {
    const x = Math.round(s * pxPerStep - offset) + 0.5;
    if (s % stepsPerBar === 0) {
      line(ctx, x, 1, x, RULER_H, "#6A6A6A");
      if (s < totalSteps) {
        ctx.fillStyle = "#C8C8C8";
        ctx.fillText(String(s / stepsPerBar + 1), x + 5, 5);
      }
    } else if (s % STEPS_PER_BEAT === 0) {
      line(ctx, x, RULER_H - 7, x, RULER_H, "#444444");
    }
  }
}

// ---------------------------------------------------------- HTML fragments
// Every control is a picture of one: aria-hidden, no handlers.
function Chevron() {
  return (
    <svg width="12" height="8" viewBox="0 0 12 8" className="ml-1.5" aria-hidden="true">
      <path d="M1.5 1.8 6 6.2l4.5-4.4" fill="none" stroke={TEXT} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// The plugin's small rotary pot (master tune / FX amount / lane pan): matte
// #2E2E32 body on two black shadows, a thin white edge, a white pointer.
function Pot({ size = 18, value = 0.5 }) {
  const r = size / 2;
  const angle = Math.PI * 1.2 + value * Math.PI * 1.6;
  const len = r - 2.5;
  const x2 = r + Math.sin(angle) * len;
  const y2 = r - Math.cos(angle) * len;
  return (
    <span
      className="relative inline-block shrink-0 rounded-full"
      style={{
        width: size,
        height: size,
        background: "#2E2E32",
        boxShadow: "0 2px 0 1px rgba(0,0,0,0.5), 0 1px 0 0 rgba(0,0,0,0.3), inset 0 0 0 0.6px rgba(255,255,255,0.12)",
      }}
    >
      <svg width={size} height={size} className="absolute inset-0" aria-hidden="true">
        <line x1={r} y1={r} x2={x2} y2={y2} stroke="rgba(255,255,255,0.85)" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    </span>
  );
}

function Label({ children, dim = false, className = "" }) {
  return (
    <span className={`whitespace-nowrap text-[12.5px] font-bold uppercase ${className}`} style={{ color: dim ? "rgba(242,242,242,0.52)" : TEXT }}>
      {children}
    </span>
  );
}

// The library drawer glyph: three upright rounded bars.
function LibraryGlyph() {
  return (
    <svg width="26" height="24" viewBox="0 0 26 24" aria-hidden="true">
      {[5, 13, 21].map((x) => (
        <line key={x} x1={x} y1="3" x2={x} y2="21" stroke="rgba(255,255,255,0.9)" strokeWidth="2.6" strokeLinecap="round" />
      ))}
    </svg>
  );
}

function MidiOutIcon() {
  return (
    <svg width="26" height="20" viewBox="0 0 26 20" aria-hidden="true">
      <rect x="1" y="1" width="24" height="18" rx="3.5" fill="none" stroke={TEXT} strokeWidth="1.5" />
      {[5.5, 9, 12.5, 16, 19.5].map((x) => (
        <line key={x} x1={x} y1="3.5" x2={x} y2="16.5" stroke={TEXT} strokeWidth="1.3" />
      ))}
      {[7.25, 10.75, 17.75].map((x) => (
        <rect key={x} x={x - 1} y="3.5" width="2" height="7" fill={TEXT} />
      ))}
    </svg>
  );
}

function AudioOutIcon() {
  const bars = [4, 7, 10, 6, 12, 8, 11, 5, 9, 4];
  return (
    <svg width="26" height="20" viewBox="0 0 26 20" aria-hidden="true">
      <rect x="1" y="1" width="24" height="18" rx="3.5" fill="none" stroke={TEXT} strokeWidth="1.5" />
      {bars.map((h, i) => (
        <line key={i} x1={4.6 + i * 1.85} y1={10 - h / 2} x2={4.6 + i * 1.85} y2={10 + h / 2} stroke={TEXT} strokeWidth="1.1" strokeLinecap="round" />
      ))}
    </svg>
  );
}

// Slow / Slap: the plugin blits a white outline image, then the black
// lettering on top, both at the button's opacity (0.52 when off).
function FxLogo({ name, on }) {
  const opacity = on ? 0.96 : 0.52;
  return (
    <span className="relative inline-block shrink-0" style={{ width: 80, height: 28, opacity }}>
      <img src={`/plugin-ui/${name}_outline.png`} alt="" className="absolute inset-0 h-full w-full object-contain" draggable="false" />
      <img src={`/plugin-ui/${name}_black.png`} alt="" className="absolute inset-0 h-full w-full object-contain" draggable="false" />
    </span>
  );
}

function TopBar({ numBars, palette }) {
  const bars = Math.max(1, numBars || 1);
  return (
    <div
      className="flex items-center border-b pl-1 pr-2"
      style={{ height: TOPBAR_H + 8, background: TOOLBAR, borderColor: DIVIDER }}
    >
      <span className="flex w-11 justify-center">
        <LibraryGlyph />
      </span>
      <img src="/plugin-ui/settings_gear.svg" alt="" width="26" height="26" className="ml-2 opacity-95" draggable="false" />
      <Label className="ml-3">Tune</Label>
      <span className="ml-3 flex items-center gap-2">
        <Pot value={0.5} />
        <Label className="normal-case">0 st</Label>
      </span>

      <span className="ml-auto flex items-center pl-6">
        <Label>Length</Label>
        <Label className="ml-2.5">
          {bars} {bars === 1 ? "Bar" : "Bars"}
        </Label>
        <Chevron />
      </span>
      <span className="ml-5 flex items-center">
        <Label>Grid</Label>
        <Label className="ml-2.5">1/16</Label>
        <Chevron />
      </span>
      <svg width="24" height="16" viewBox="0 0 24 16" className="ml-5 shrink-0" aria-hidden="true">
        <path d="M2 8h18M14 2.5 20 8l-6 5.5" fill="none" stroke={TEXT} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      <span className="ml-5 flex items-center">
        <span className="mr-2 inline-block rounded-[1px]" style={{ width: 4, height: 16, background: css(laneColour(0, palette)) }} />
        <Label>Pattern 1</Label>
        <Chevron />
      </span>

      <span className="ml-3 flex items-center gap-1.5">
        <FxLogo name="slow" on={false} />
        <Pot value={0} />
        <Label dim>0%</Label>
        <Label dim className="ml-2">
          1/2
        </Label>
        <Chevron />
      </span>
      <span className="ml-3 flex items-center gap-1.5">
        <FxLogo name="slap" on={false} />
        <Pot value={0} />
        <Label dim>0%</Label>
      </span>

      <span className="ml-4 flex items-center gap-2">
        <MidiOutIcon />
        <Label>Midi out</Label>
      </span>
      <span className="ml-4 flex items-center gap-2">
        <AudioOutIcon />
        <Label>Audio out</Label>
        <Chevron />
      </span>
    </div>
  );
}

function laneLabel(lane) {
  // Privacy: never the producer's own lane name (often a sample pack's
  // filename), only the classifier's generic category, or "Other".
  const raw = lane?.category;
  const trimmed = typeof raw === "string" ? raw.trim() : "";
  return trimmed || "Other";
}

function LaneRow({ index, lane, meterRef, palette }) {
  const faderLeft = LANE_PANEL_W - (FADER_W + 8 + PAN_SIZE + PAN_GAP);
  const soloLeft = faderLeft - (SOLO_W + 2);
  const thumbX = FADER_W * UNITY_FADER_POS; // a lane at unity gain, the plugin's default
  return (
    <div className="relative" style={{ height: CELL_H, borderBottom: `1px solid ${GRID_LINE}` }}>
      <span
        className="absolute rounded-[2px]"
        style={{ left: 6, top: (CELL_H - 18) / 2, width: 10, height: 18, background: css(laneColour(index, palette)) }}
      />
      <span
        className="absolute truncate text-[13px] font-bold leading-none"
        style={{ left: 22, top: 0, height: CELL_H, lineHeight: `${CELL_H}px`, width: soloLeft - 22 - 4, color: TEXT }}
      >
        {laneLabel(lane)}
      </span>
      {/* Solo: a bold "S" at 40% white over a faint glow. */}
      <span
        className="absolute text-center text-[14px] font-bold"
        style={{
          left: soloLeft,
          top: 0,
          width: SOLO_W,
          lineHeight: `${CELL_H}px`,
          color: "rgba(255,255,255,0.40)",
          textShadow: "0 0 1px rgba(255,255,255,0.06), 0 0 2px rgba(255,255,255,0.06)",
        }}
      >
        S
      </span>
      {/* Fader: the 6px meter track, the live meter fill, the matte thumb. */}
      <span className="absolute" style={{ left: faderLeft, top: CELL_H / 2 - 3, width: FADER_W, height: 6 }}>
        <span className="absolute inset-0 rounded-[2px]" style={{ background: "rgba(255,255,255,0.08)" }} />
        <span ref={meterRef} className="absolute left-0 top-0 block overflow-visible" style={{ width: 0, height: 6 }}>
          <span
            className="absolute inset-x-0 block rounded-[2.5px]"
            style={{ top: -2.5, height: 11, backgroundImage: METER_GRADIENT, backgroundSize: `${FADER_W}px 100%`, opacity: 0.35 }}
          />
          <span
            className="absolute inset-x-0 block rounded-[2px]"
            style={{ top: -1, height: 8, backgroundImage: METER_GRADIENT, backgroundSize: `${FADER_W}px 100%`, opacity: 0.85 }}
          />
        </span>
        <span
          className="absolute rounded-full"
          style={{
            left: thumbX - 6,
            top: -3,
            width: 12,
            height: 12,
            background: "#2E2E32",
            boxShadow: "0 2px 0 1px rgba(0,0,0,0.5), 0 1px 0 0 rgba(0,0,0,0.3), inset 0 0 0 0.6px rgba(255,255,255,0.12)",
          }}
        />
      </span>
      <span className="absolute" style={{ left: LANE_PANEL_W - (PAN_SIZE + 8), top: (CELL_H - PAN_SIZE) / 2 }}>
        <Pot size={16} value={0.5} />
      </span>
    </div>
  );
}

// The plugin's horizontal scrollbar: a rounded dark track, a lighter thumb
// with a small grip in its middle. A picture, driven by the real scroll.
function ScrollBar({ thumbRef }) {
  return (
    <div className="flex items-center px-2" style={{ height: SCROLLBAR_H, background: BG }}>
      <div className="relative h-[11px] w-full rounded-full" style={{ background: "#232326", boxShadow: "inset 0 0 0 1px #34343A" }}>
        <div ref={thumbRef} className="absolute top-[1px] flex h-[9px] items-center justify-center rounded-full" style={{ left: 0, width: 40, background: "#47474C" }}>
          <span className="text-[8px] leading-none tracking-[1px] text-white/50">···</span>
        </div>
      </div>
    </div>
  );
}

function EditorHeader() {
  return (
    <div className="relative border-b" style={{ height: EDITOR_HEADER_H, background: TOOLBAR, borderColor: DIVIDER }}>
      <span className="absolute flex items-center" style={{ left: 330, top: 0, height: EDITOR_HEADER_H }}>
        <Label>Grid</Label>
        <Label className="ml-4">1/64</Label>
        <Chevron />
      </span>
      <span className="absolute flex items-center" style={{ left: 480, top: 0, height: EDITOR_HEADER_H }}>
        <Label>Cut</Label>
        <Label className="ml-4">Grid</Label>
        <Chevron />
      </span>
      <span className="absolute flex items-center" style={{ left: 640, top: 0, height: EDITOR_HEADER_H }}>
        <Label>Velocity</Label>
      </span>
      <span className="absolute flex items-center" style={{ right: 150, top: 0, height: EDITOR_HEADER_H }}>
        <Label>Pattern chain</Label>
      </span>
      <span className="absolute flex items-center" style={{ right: 26, top: 0, height: EDITOR_HEADER_H }}>
        <Label>Piano roll</Label>
      </span>
    </div>
  );
}

function PianoKeys() {
  return (
    <div className="shrink-0" style={{ width: KEYS_W, background: BG }}>
      <div style={{ height: RULER_H }} />
      {PIANO_ROWS.map((n) => {
        const black = n.includes("#");
        const isC = n === "C2";
        return (
          <div key={n} className="flex items-center pl-1" style={{ height: KEY_ROW_H }}>
            <span
              className="flex h-[14px] items-center rounded-[3px] pl-1 text-[9px] font-bold"
              style={
                black
                  ? { width: 30, background: "linear-gradient(#3C3C40, #2A2A2E)", color: "#C8C8C8" }
                  : { width: 44, background: isC ? "linear-gradient(#FFFFFF, #E4E4E4)" : "linear-gradient(#DADADA, #BDBDBD)", color: "#2A2A2A" }
              }
            >
              {n}
            </span>
          </div>
        );
      })}
    </div>
  );
}

// -------------------------------------------------------------- component
export default function PluginWindow({ grid: rawGrid, getPosition, isPlaying = false, fitToViewport = false, palette = "classic" }) {
  const grid = useMemo(() => withoutEmptyLanes(rawGrid), [rawGrid]);
  const lanes = grid?.lanes?.length ? grid.lanes : [{}];
  const laneCount = lanes.length;
  const stepsPerBar = (grid?.beatsPerBar || 4) * STEPS_PER_BEAT;
  const totalSteps = Math.max(1, Math.round(loopBeats(grid) * STEPS_PER_BEAT));
  const gridW = DESIGN_W - LANE_PANEL_W - 1;
  const pianoW = DESIGN_W - KEYS_W;
  const cellW = gridW / (stepsPerBar * VISIBLE_BARS);
  const totalW = totalSteps * cellW;
  const gridH = RULER_H + (laneCount + 1) * CELL_H; // +1: the plugin's "+" row

  const wrapRef = useRef(null);
  const [scale, setScale] = useState(1);
  const gridCanvasRef = useRef(null);
  const pianoCanvasRef = useRef(null);
  const gridThumbRef = useRef(null);
  const pianoThumbRef = useRef(null);
  const meterRefs = useRef([]);
  const scrollRef = useRef(0); // the view's left edge, in grid px

  // The window is laid out at DESIGN_W and scaled to fit, so a phone sees
  // the same interface, smaller, rather than a reflowed one. With
  // fitToViewport it also shrinks to fit the screen's height below its own
  // top edge, so the whole window shows at load without scrolling (never
  // below MIN_FIT_SCALE, where it would get too small to read: past that the
  // page scrolls instead).
  const windowH = TOPBAR_H + 8 + gridH + SCROLLBAR_H + EDITOR_HEADER_H + PIANO_H + SCROLLBAR_H;
  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return undefined;
    const measure = () => {
      let next = Math.min(1, el.clientWidth / (DESIGN_W + 2));
      if (fitToViewport) {
        const top = el.getBoundingClientRect().top + window.scrollY;
        const room = window.innerHeight - top - 16;
        next = Math.min(next, Math.max(MIN_FIT_SCALE, room / (windowH + 2)));
      }
      setScale(next);
    };
    measure();
    window.addEventListener("resize", measure);
    if (typeof ResizeObserver === "undefined") return () => window.removeEventListener("resize", measure);
    const ro = new ResizeObserver(() => requestAnimationFrame(measure));
    ro.observe(el);
    return () => {
      window.removeEventListener("resize", measure);
      ro.disconnect();
    };
  }, [fitToViewport, windowH]);

  // The piano roll shows the plugin's selected lane: here, the first one.
  const active = useMemo(() => {
    const byLane = new Map();
    for (const evt of grid?.events || []) {
      const step = eventStep(evt, grid, STEPS_PER_BEAT);
      if (step === null || !Number.isFinite(evt?.lane)) continue;
      const lane = Math.floor(evt.lane);
      if (lane < 0 || lane >= laneCount) continue;
      if (!byLane.has(lane)) byLane.set(lane, new Map());
      byLane.get(lane).set(step, evt);
    }
    return byLane;
  }, [grid, laneCount]);

  useLayoutEffect(() => {
    const gc = gridCanvasRef.current;
    const pc = pianoCanvasRef.current;
    if (!gc || !pc || !grid) return undefined;

    const loopSec = loopSeconds(grid);
    const levels = new Float32Array(laneCount);
    let prevStep = null;
    let prevTime = performance.now();
    let lastPage = -1;
    let raf = 0;

    const trigger = (step) => {
      for (let lane = 0; lane < laneCount; lane += 1) {
        const evt = active.get(lane)?.get(step);
        if (!evt) continue;
        const v = Math.max(1, Math.min(127, Number.isFinite(evt.vel) ? evt.vel : 100));
        levels[lane] = Math.max(levels[lane], (v / 127) * METER_HEADROOM);
      }
    };

    const drawGrid = (ctx, offset, headAbs) => {
      ctx.fillStyle = BG;
      ctx.fillRect(0, 0, gridW, gridH);
      drawRuler(ctx, gridW, offset, cellW, totalSteps, stepsPerBar);
      const first = Math.max(0, Math.floor(offset / cellW));
      const last = Math.min(totalSteps - 1, Math.ceil((offset + gridW) / cellW));
      for (let lane = 0; lane < laneCount; lane += 1) {
        const y = RULER_H + lane * CELL_H;
        const colour = laneColour(lane, palette);
        const laneMap = active.get(lane);
        for (let s = first; s <= last; s += 1) {
          const cell = { x: s * cellW - offset + 1, y: y + 1, w: cellW - 2, h: CELL_H - 2 };
          const groupStart = s % 4 === 0;
          const evenGroup = Math.floor(s / 4) % 2 === 0;
          let bg = evenGroup ? hex("#2B2B2B") : hex("#252525");
          if (groupStart) bg = brighter(bg, 0.08);
          ctx.fillStyle = css(bg);
          rr(ctx, cell.x, cell.y, cell.w, cell.h, CELL_CORNER);
          ctx.fill();
          ctx.strokeStyle = evenGroup ? GRID_LINE : GRID_LINE_BRIGHT;
          ctx.lineWidth = groupStart ? 1.2 : 0.9;
          ctx.stroke();
          const evt = laneMap?.get(s);
          if (evt) drawActive(ctx, cell, colour, evt.vel, groupStart);
          else drawInactive(ctx, cell, groupStart);
        }
      }
      const hx = headAbs - offset;
      if (isPlaying && hx >= -2 && hx <= gridW + 2) {
        ctx.fillStyle = PLAYHEAD;
        ctx.fillRect(hx, 0, 2, RULER_H + laneCount * CELL_H);
      }
    };

    const drawPiano = (ctx, offset, headAbs) => {
      // Same timeline as the grid (px per beat), its own wider view.
      const pxPerStep = cellW;
      ctx.fillStyle = BG;
      ctx.fillRect(0, 0, pianoW, PIANO_H);
      drawRuler(ctx, pianoW, offset, pxPerStep, totalSteps, stepsPerBar);
      PIANO_ROWS.forEach((n, i) => {
        const y = RULER_H + i * KEY_ROW_H;
        ctx.fillStyle = n === "C2" ? "#292929" : n.includes("#") ? "#212121" : "#262626";
        ctx.fillRect(0, y, pianoW, KEY_ROW_H);
        line(ctx, 0, y + KEY_ROW_H - 0.5, pianoW, y + KEY_ROW_H - 0.5, "#1C1C1C");
      });
      const first = Math.max(0, Math.floor(offset / pxPerStep));
      const last = Math.min(totalSteps, Math.ceil((offset + pianoW) / pxPerStep));
      for (let s = first; s <= last; s += 1) {
        if (s % STEPS_PER_BEAT !== 0) continue;
        const x = Math.round(s * pxPerStep - offset) + 0.5;
        line(ctx, x, RULER_H, x, PIANO_H, s % stepsPerBar === 0 ? "#3A3A3A" : "#2E2E2E");
      }
      // The selected lane's hits, as the plugin draws notes: a rounded bar
      // in the lane colour, lit from the centre.
      const colour = laneColour(0, palette);
      const y = RULER_H + ROOT_ROW * KEY_ROW_H + 1.5;
      const h = KEY_ROW_H - 3;
      for (const evt of grid?.events || []) {
        if (Math.floor(evt?.lane) !== 0) continue;
        const start = Number.isFinite(evt.start) ? evt.start : 0;
        const len = Math.max(0.0625, Number.isFinite(evt.length) && evt.length > 0 ? evt.length : 0.25);
        const x = start * STEPS_PER_BEAT * pxPerStep - offset;
        const w = Math.max(3, len * STEPS_PER_BEAT * pxPerStep - 1);
        if (x > pianoW || x + w < 0) continue;
        const g = ctx.createLinearGradient(x, 0, x + w, 0);
        g.addColorStop(0, css(darker(colour, 0.25)));
        g.addColorStop(0.5, css(brighter(colour, 0.15)));
        g.addColorStop(1, css(darker(colour, 0.25)));
        ctx.fillStyle = g;
        rr(ctx, x, y, w, h, 3);
        ctx.fill();
        ctx.strokeStyle = "rgba(0,0,0,0.35)";
        ctx.lineWidth = 1;
        ctx.stroke();
      }
      const hx = headAbs - offset;
      if (isPlaying && hx >= -2 && hx <= pianoW + 2) {
        ctx.fillStyle = PLAYHEAD;
        ctx.fillRect(hx, 0, 2, PIANO_H);
      }
    };

    const placeThumb = (el, viewW, offset) => {
      if (!el) return;
      const track = el.parentElement?.clientWidth || viewW;
      const frac = Math.min(1, viewW / Math.max(viewW, totalW));
      const w = Math.max(24, track * frac);
      const maxOff = Math.max(1, totalW - viewW);
      const left = totalW > viewW ? (Math.min(offset, maxOff) / maxOff) * (track - w) : 0;
      el.style.width = `${w}px`;
      el.style.left = `${left}px`;
    };

    const frame = () => {
      const t = getPosition?.() ?? 0;
      const headAbs = playheadX(t, grid, totalW);

      // Follow mode: page by whole views, like the plugin.
      if (isPlaying && totalW > gridW) {
        const page = Math.floor(headAbs / gridW);
        if (page !== lastPage) {
          lastPage = page;
          scrollRef.current = Math.min(totalW - gridW, page * gridW);
        }
      } else if (!isPlaying) {
        lastPage = -1;
      }
      const offset = scrollRef.current;

      drawGrid(sizeCanvas(gc, gridW, gridH), offset, headAbs);
      drawPiano(sizeCanvas(pc, pianoW, PIANO_H), offset, headAbs);
      placeThumb(gridThumbRef.current, gridW, offset);
      placeThumb(pianoThumbRef.current, pianoW, offset);

      // Lane meters, driven by the pattern's own steps and velocities (the
      // site has the full mix only, never per-lane audio).
      const now = performance.now();
      const decay = Math.pow(METER_DECAY, ((now - prevTime) / 1000) * 30);
      prevTime = now;
      for (let lane = 0; lane < laneCount; lane += 1) levels[lane] *= decay;
      if (isPlaying && loopSec > 0) {
        const step = Math.floor(((((t % loopSec) + loopSec) % loopSec) / loopSec) * totalSteps) % totalSteps;
        if (prevStep === null) trigger(step);
        else if (step !== prevStep) {
          for (let s = (prevStep + 1) % totalSteps; ; s = (s + 1) % totalSteps) {
            trigger(s);
            if (s === step) break;
          }
        }
        prevStep = step;
      } else {
        prevStep = null;
      }
      for (let lane = 0; lane < laneCount; lane += 1) {
        const el = meterRefs.current[lane];
        if (el) el.style.width = `${Math.max(0, Math.min(1, levels[lane])) * FADER_W}px`;
      }

      raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
  }, [grid, getPosition, isPlaying, laneCount, active, cellW, totalW, totalSteps, stepsPerBar, gridW, gridH, pianoW, palette]);

  // Scrolling by wheel/trackpad still works, horizontally, like the plugin.
  const onWheel = (e) => {
    const d = Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.shiftKey ? e.deltaY : 0;
    if (!d) return;
    scrollRef.current = Math.max(0, Math.min(Math.max(0, totalW - gridW), scrollRef.current + d / scale));
  };

  return (
    <div ref={wrapRef} className="w-full select-none" style={{ height: (windowH + 2) * scale }} aria-label="Pattern preview in the TrapMan interface" role="img">
      <div
        className="overflow-hidden rounded-[10px] border"
        style={{
          width: DESIGN_W + 2,
          // Centred when the height fit makes it narrower than the page.
          marginLeft: `max(0px, calc((100% - ${(DESIGN_W + 2) * scale}px) / 2))`,
          transform: `scale(${scale})`,
          transformOrigin: "top left",
          background: BG,
          borderColor: "#3A3A3E",
          fontFamily: FONT,
          boxShadow: "0 20px 60px rgba(0,0,0,0.5)",
        }}
        onWheel={onWheel}
      >
        <TopBar numBars={grid?.numBars} palette={palette} />
        <div className="flex">
          <div className="shrink-0 border-r" style={{ width: LANE_PANEL_W, background: PANEL, borderColor: DIVIDER }}>
            <div style={{ height: RULER_H, background: BG, borderBottom: `1px solid ${DIVIDER}` }} />
            {lanes.map((lane, i) => (
              <LaneRow
                key={i}
                index={i}
                lane={lane}
                palette={palette}
                meterRef={(el) => {
                  meterRefs.current[i] = el;
                }}
              />
            ))}
            <div className="flex items-center" style={{ height: CELL_H, paddingLeft: 8 }}>
              <span className="flex h-[22px] w-[22px] items-center justify-center rounded-[4px] text-[16px] font-bold leading-none" style={{ background: "#3A3A3E", color: TEXT }}>
                +
              </span>
            </div>
          </div>
          <canvas ref={gridCanvasRef} style={{ width: gridW, height: gridH }} className="block" aria-hidden="true" />
        </div>
        <ScrollBar thumbRef={gridThumbRef} />
        <EditorHeader />
        <div className="flex">
          <PianoKeys />
          <canvas ref={pianoCanvasRef} style={{ width: pianoW, height: PIANO_H }} className="block" aria-hidden="true" />
        </div>
        <ScrollBar thumbRef={pianoThumbRef} />
      </div>
    </div>
  );
}
